import { require_manager } from "@/utils/request";
import { ref } from "vue";

// 复导出纯函数，保持向后兼容
export { to_permission_identifier, compute_item_permission_key, compute_route_permission_key } from "@/utils/permission_utils";

/**
 * Permission manager class for fetching, validating, and caching the current user's permissions.
 * Permissions are loaded once on initialization and cached for the lifetime of the application.
 * Call clear() on user logout to reset the cache.
 */
class PermissionManager {

    constructor() {
        this._permissions = ref([]);
        this._load_promise = null;
        this._init_load();
    }

    /**
     * Kicks off the initial permission load immediately upon construction.
     */
    async _init_load() {
        this._load_promise = this._fetch_permissions();
        await this._load_promise;
        this._load_promise = null;
    }

    /**
     * Fetches the current user's permission list from the backend
     * and updates the reactive permissions ref.
     *
     * @returns {Promise<void>}
     */
    async _fetch_permissions() {
        const response = await require_manager.get('permissions/get_user_permissions', {});
        if (response) {
            this._permissions.value = response;
        } else {
            this._permissions.value = [];
        }
    }

    /**
     * Ensures permissions are loaded before proceeding.
     * Useful in script logic or route guards where permissions must be available.
     *
     * @returns {Promise<void>}
     */
    async ensure_loaded() {
        if (this._load_promise) {
            await this._load_promise;
        }
    }

    /**
     * Validates whether the current user has a specific permission.
     * Synchronous method that checks against the cached permissions.
     * The reactive ref ensures templates will re-render when permissions are loaded.
     *
     * @param {string} permission_key - The permission identifier to check (e.g., 'user.create').
     * @returns {Boolean} True if the user has the permission, false otherwise.
     */
    valid(permission_key) {
        return this._permissions.value.includes(permission_key);
    }

    /**
     * Clears cached permissions and re-fetches from backend.
     * Should be called on user logout or when permissions change.
     */
    async reload() {
        this._permissions.value = [];
        this._load_promise = this._fetch_permissions();
        await this._load_promise;
        this._load_promise = null;
    }

    /**
     * Clears all cached permissions without re-fetching.
     * Should be called on user logout to prevent stale data.
     */
    clear() {
        this._permissions.value = [];
        this._load_promise = null;
    }

}


export const permission_manager = new PermissionManager()